import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useTagsByDocument, TagContextInfo, TagUnit } from 'entities/tag';
import { i18n } from 'shared/i18n';
import { StatusBadge, ValidationBadge, OriginBadge } from './TagBadges';

interface ValuesPaneProps {
  documentSuperId: number | undefined;
  selectedConceptName: string | null;
  extensionPrefix?: string | null;
}

const formatDate = (dateValue: string) =>
  new Date(dateValue).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' }).replace(/\./g, '');

const periodLabel = (context: TagContextInfo | null | undefined): string => {
  if (!context) return '';
  if (context.periodInstant) return formatDate(context.periodInstant);
  if (context.periodStartDate && context.periodEndDate) return `${formatDate(context.periodStartDate)} – ${formatDate(context.periodEndDate)}`;
  if (context.periodForever) return i18n('tag:treeOverview.values.forever');
  return '';
};

const unitLabel = (unit: TagUnit | null | undefined): string => {
  if (!unit) return '';
  if (unit.divideUnitNominator && unit.divideUnitDenominator) return `${unit.divideUnitNominator}/${unit.divideUnitDenominator}`;
  return unit.measure ?? '';
};

export const ValuesPane = ({ documentSuperId, selectedConceptName, extensionPrefix }: ValuesPaneProps) => {
  const { t } = useTranslation('tag');

  const { data } = useTagsByDocument(documentSuperId, { include: ['concept', 'context', 'unit', 'metadata'] });

  const facts = useMemo(() => {
    if (!selectedConceptName || !data?.tags) return [];
    const prefixed = extensionPrefix ? `${extensionPrefix}:${selectedConceptName}` : null;
    return data.tags.filter((t) => t.concept?.name === selectedConceptName || (!!prefixed && t.concept?.name === prefixed));
  }, [data, selectedConceptName, extensionPrefix]);

  if (!selectedConceptName) {
    return <div className="tree-overview__values-empty">{t('treeOverview.values.prompt')}</div>;
  }

  if (facts.length === 0) {
    return <div className="tree-overview__values-empty">{t('treeOverview.values.none')}</div>;
  }

  return (
    <table className="tree-overview__values-table">
      <thead>
        <tr>
          <th>{t('treeOverview.values.value')}</th>
          <th>{t('treeOverview.values.period')}</th>
          <th>{t('treeOverview.values.unit')}</th>
          <th>{t('treeOverview.values.status')}</th>
          <th>{t('treeOverview.values.validation')}</th>
          <th>{t('treeOverview.values.origin')}</th>
        </tr>
      </thead>
      <tbody>
        {facts.map((fact) => (
          <tr key={fact.id}>
            <td>{fact.metadata?.textSnippet || fact.value || ''}</td>
            <td>{periodLabel(fact.context)}</td>
            <td>{unitLabel(fact.unit)}</td>
            <td>
              <StatusBadge status={fact.status} />
            </td>
            <td>
              <ValidationBadge status={fact.validationStatus} />
            </td>
            <td>
              <OriginBadge origin={fact.origin} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
